import http from './http';
import { toDateStr, withDateTimes, withDates } from '../utils/dates';

/* Tanki servisi nad REST API-jem. Datumi se uvek šalju kao 'YYYY-MM-DD' / 'YYYY-MM-DD HH:mm'. */

const d = (r) => r.data;

export const usersApi = {
    authenticate: (email, password) => http.post('/users/authenticate', { email, password }).then(d),
    register: (u) => http.post('/users/register', u).then(d),
};

export const emailApi = {
    resend: (email) => http.post('/email/verify', { email }).then(d),
    confirm: (id, code) => http.get(`/email/confirm/${id}/${code}`).then(d),
};

export const passwordApi = {
    request: (email) => http.post('/passwordrecovery/request', { email }).then(d),
    verify: (id, code) => http.get(`/passwordrecovery/verify/${id}/${code}`).then(d),
    update: (id, code, password) => http.put(`/passwordrecovery/update/${id}`, { code, password }).then(d),
};

export const lookupApi = {
    get: (type) => http.get(`/lookup/${type}`).then(d),
};

export const companiesApi = {
    all: () => http.get('/companies/all').then(d),
    get: (id) => http.get(`/companies/${id}`).then(d),
    create: (c) => http.post('/companies/new', withDates(c)).then(d),
    update: (c) => http.put(`/companies/${c.id}`, withDates(c)).then(d),
    remove: (id) => http.delete(`/companies/${id}`).then(d),
};

export const vehiclesApi = {
    all: () => http.get('/vehicles/all').then(d),
    get: (id) => http.get(`/vehicles/${id}`).then(d),
    create: (v) => http.post('/vehicles/new', v).then(d),
    update: (v) => http.put(`/vehicles/${v.id}`, v).then(d),
    remove: (id) => http.delete(`/vehicles/${id}`).then(d),
    makes: () => http.get('/vehicles/makes/all').then(d),
    createMake: (name) => http.post('/vehicles/makes/new', { id: 0, name }).then(d),
    createModel: (name, makeId) => http.post('/vehicles/models/new', { id: 0, name, makeId }).then(d),
};

export const driversApi = {
    all: (query) => http.post('/drivers/all', query).then(d),
    get: (id) => http.get(`/drivers/${id}`).then(d),
    create: (x) => http.post('/drivers/new', withDates(x)).then(d),
    update: (x) => http.put(`/drivers/${x.id}`, withDates(x)).then(d),
    remove: (id) => http.delete(`/drivers/${id}`).then(d),
    activities: (id, start, end) =>
        http.get(`/drivers/${id}/activities`, { params: { startDate: toDateStr(start), endDate: toDateStr(end) } }).then(d),
    activityDates: (id) => http.get(`/drivers/${id}/activities/dates`).then(d),
    saveActivities: (payload) => http.post('/drivers/activities', withDateTimes(payload)).then(d),
    addActivity: (payload) => http.post('/drivers/activities/new', withDateTimes(payload)).then(d),
    addMultiple: (payload) => http.post('/drivers/activities/multiple', withDateTimes(payload)).then(d),
    totals: (ddActivity) => http.post('/drivers/activities/totals', withDateTimes(ddActivity)).then(d),
};

export const specificConditionApi = {
    get: (driverId, start, end) =>
        http.get(`/specificcondition/${driverId}`, { params: { startDate: toDateStr(start), endDate: toDateStr(end) } }).then(d),
};

export const tachoApi = {
    importFiles: (files, url = '/tacho') => {
        const fd = new FormData();
        files.forEach((f) => fd.append(f.name, f));
        return http.post(url, fd).then(d);
    },
    importVehicle: (file, from, to) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('from', from.toISOString());
        fd.append('to', to.toISOString());
        return http.post('/tacho/vehicles', fd).then(d);
    },
    storeVehicles: (drivers) => http.post('/tacho/storevehicles', withDateTimes(drivers)).then(d),
    autoGenerateBreak: (driverId, start, end) =>
        http.post('/tacho/autogeneratebreak', { driverId, startDate: toDateStr(start), endDate: toDateStr(end) }).then(d),
};

export const exportApi = {
    /* silent: greška je blob (text/plain) — formular je sam čita (blobError) i prikazuje toast */
    blob: (url, body) => http.post(url, body, { responseType: 'blob', silent: true }),
    report: (body) => exportApi.blob('/export/data', withDates(body)),
    psychostate: (body) => exportApi.blob('/export/data/psychostate', withDates(body)),
    detectAttestation: (body) => http.post('/export/data/detectattestationactivities', withDates(body)).then(d),
    attestation: (body) => exportApi.blob('/export/data/activitiesattestation', withDateTimes(body)),
};

export const kazneApi = {
    /* aktivnosti + OUT zapisi za obračun (kontekst 28 dana pre početka perioda); obračun radi Kazne/engine */
    data: (body) => http.post('/kazne/data', withDates(body)).then(d),
    /* udvojena posada iz uvezenih kartica (.ddd): [{driverId, od, do}] — greška ne sme da zaustavi obračun */
    posada: (body) => http.post('/kazne/posada', withDates(body), { silent: true }).then(d).catch(() => []),
    mesta: (body) => http.post('/kazne/mesta', withDates(body), { silent: true }).then(d).catch(() => []),
    settings: () => http.get('/kazne/settings').then(d),
    saveSettings: (items) => http.put('/kazne/settings', { items }).then(d),
};

export const ocitavanjaApi = {
    /* poslednja očitavanja kartica (28 dana) i tahografa (90 dana) po firmi; status računa Ocitavanja/rokovi.js */
    overview: (body) => http.post('/ocitavanja/overview', body).then(d),
};

export const akcizeApi = {
    /* refakcija akcize na gorivo: tabela vozila za kvartal (km iz tahografa; bez DDD-a procena koju operater potvrđuje), gorivo po vozilu + kontrole */
    tabela: (params) => http.get('/akcize/tabela', { params }).then(d),
    /* uvoz Excel evidencija: analiza (pregled, ništa se ne upisuje), pa snimanje izabrane vrste */
    analiza: (body) => http.post('/akcize/uvoz/analiza', body).then(d),
    uvozNabavke: (body) => http.post('/akcize/uvoz/nabavke', body).then(d),
    uvozIzdavanja: (body) => http.post('/akcize/uvoz/izdavanja', body).then(d),
    sacuvajNabavku: (body) => http.post('/akcize/nabavka', body).then(d),
    obrisiNabavku: (id) => http.delete(`/akcize/nabavka/${id}`).then(d),
    sacuvajIzdavanje: (body) => http.post('/akcize/izdavanje', body).then(d),
    obrisiIzdavanje: (id) => http.delete(`/akcize/izdavanje/${id}`).then(d),
    /* km za mesec koje operater potvrđuje (ili stanje kilometar-sata na početku i kraju meseca) */
    sacuvajKmMesec: (body) => http.post('/akcize/km-mesec', body).then(d),
    obrisiKmMesec: (body) => http.post('/akcize/km-mesec/obrisi', body).then(d),
};

export const listiciApi = {
    /* bulk unos analognih listića: prepoznavanje radi klijent (TachoBulk/engine), server čuva dane i evidenciju */
    check: (body) => http.post('/listici/check', body).then(d),
    save: (body) => http.post('/listici/save', withDateTimes(body)).then(d),
    izbaci: (body) => http.post('/listici/izbaci', body).then(d),
    list: (body) => http.post('/listici/list', withDates(body)).then(d),
    setStatus: (ids, status) => http.put('/listici/status', { ids, status }).then(d),
    /* AI čitač rukopisa (lokalni model na serveru): kandidati su vozači izabranih kompanija */
    procitajVozaca: (body) => http.post('/listici/procitaj-vozaca', body).then(d),
    procitajDatum: (body) => http.post('/listici/procitaj-datum', body).then(d),
    procitajDatume: (body) => http.post('/listici/procitaj-datume', body).then(d),
};

export const podesavanjaApi = {
    /* "zabava": admin pali efekte kod operatera; operateri dobijaju promene uživo (zabava/zabavaTok.js) */
    zabava: () => http.get('/podesavanja/zabava/admin').then(d),
    sacuvajZabavu: (cfg) => http.put('/podesavanja/zabava', cfg).then(d),
    korisnici: () => http.get('/users/all').then(d),
};
